import sha256 from 'js-sha256';
import configDetails from '../Config/Config';
import { Auth } from 'aws-amplify';
import logger from './Logger';

const REDIRECT_URI = `${window.location.origin}/home`;
const SCOPES = 'Calendars.ReadWrite offline_access';

function generateCodeVerifier() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const length = 64;
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  let verifier = '';
  for (let i = 0; i < length; i++) {
    verifier += chars[randomValues[i] % chars.length];
  }
  return verifier;
}

async function generateCodeChallenge(codeVerifier) {
  const hash = sha256.array(codeVerifier);
  let binary = '';
  for (let i = 0; i < hash.length; i++) {
    binary += String.fromCharCode(hash[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function generateOutlookAuthUrl(clientId) {
  if (!clientId) {
    throw new Error('Client ID is required');
  }

  try {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      scope: SCOPES,
      response_mode: 'query',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const authUrl = `${configDetails.outlookAuthorizeEndpoint}?${params.toString()}`;
   
    return {
      url: authUrl,
      codeVerifier,
    };
  } catch (error) {
    throw error;
  }
}

export async function exchangeCodeOnBackend(code, codeVerifier) {
  if (!code || !codeVerifier) {
    throw new Error('Code and code verifier are required');
  }

  const userId = await getUserId();
  const headers = await getAuthHeaders();

  const payload = {
    code,
    code_verifier: codeVerifier,
    redirect_uri: REDIRECT_URI,
    user_id: userId,
  };

  logger.info('Exchanging Outlook code on backend', {
    component: 'outlookAuth',
    action: 'EXCHANGE_CODE_ON_BACKEND',
    context: { userId },
  });

  const response = await fetch(
    `${configDetails.outlookBackendApiBaseUrl}${configDetails.outlookExchangeEndpoint}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    logger.error('Backend token exchange failed', {
      component: 'outlookAuth',
      action: 'EXCHANGE_CODE_FAILED',
      context: { status: response.status, errorText },
    });

    // Map common status codes to user-friendly messages
    if (response.status === 400) {
      const errorObj = tryParseJson(errorText);
      if (errorObj?.error === 'invalid_grant' || errorObj?.message?.includes('expired')) {
        throw new Error('Authorization failed - code may have expired. Try again.');
      }
      throw new Error(`Invalid request: ${errorObj?.message || errorText}`);
    }
    if (response.status === 401) {
      throw new Error('Session expired. Please reconnect Outlook.');
    }
    if (response.status >= 500) {
      throw new Error('Server error. Please try again later.');
    }

    throw new Error(`Token exchange failed (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  logger.info('Outlook code exchanged successfully on backend', {
    component: 'outlookAuth',
    action: 'EXCHANGE_CODE_SUCCESS',
  });
  return data;
}

// Helper to parse JSON safely
function tryParseJson(str) {
  try { return JSON.parse(str); } catch { return {}; }
}

async function getAuthHeaders() {
  try {
    const currentUser = await Auth.currentAuthenticatedUser();
    const token = currentUser.signInUserSession.idToken.jwtToken;
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  } catch (error) {
    logger.error('Failed to get auth token for Outlook backend API', {
      component: 'outlookAuth',
      action: 'GET_AUTH_HEADERS_FAILED',
      error,
    });
    throw new Error('Authentication required. Please log in.');
  }
}

async function getUserId() {
  try {
    const currentUser = await Auth.currentAuthenticatedUser();
    return currentUser.attributes.sub;
  } catch (error) {
    throw new Error('Authentication required. Please log in.');
  }
}

export async function fetchMeetingsFromBackend() {
  try {
    const userId = await getUserId();
    const headers = await getAuthHeaders();
    logger.info('Fetching meetings from backend', {
      component: 'outlookAuth',
      action: 'FETCH_MEETINGS',
      context: { userId },
    });
    const response = await fetch(
      `${configDetails.outlookBackendApiBaseUrl}${configDetails.outlookMeetingsEndpoint}/${userId}`,
      {
        method: 'GET',
        headers,
      }
    );
    if (response.status === 404) {
      const hasConnectedBefore = localStorage.getItem('outlook_connected') === 'true';
      logger.info('No meetings found for user', {
        component: 'outlookAuth',
        action: 'FETCH_MEETINGS_EMPTY',
        context: { hasConnectedBefore },
      });
      return { connected: hasConnectedBefore, events: [] };
    }
    if (response.status === 409) {
      const errorObj = tryParseJson(await response.text());
      logger.warn('Outlook connection needs reauth', {
        component: 'outlookAuth',
        action: 'FETCH_MEETINGS_NEEDS_REAUTH',
        context: errorObj,
      });
      return {
        connected: false,
        needsReauth: true,
        events: [],
        error: 'Your Outlook connection needs to be refreshed. Please reconnect.',
      };
    }
    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Failed to fetch meetings from backend', {
        component: 'outlookAuth',
        action: 'FETCH_MEETINGS_FAILED',
        context: { status: response.status, errorText },
      });
      if (response.status === 401) {
        return { connected: false, events: [], error: 'Session expired. Please reconnect Outlook.' };
      }
      throw new Error(`Failed to fetch meetings (${response.status})`);
    }
    const data = await response.json();
    
    let rawEvents = [];
    if (data.meetings && Array.isArray(data.meetings)) {
      rawEvents = data.meetings;
    } else if (Array.isArray(data)) {
      rawEvents = data;
    } else if (data.value && Array.isArray(data.value)) {
      rawEvents = data.value;
    }

    logger.info('Meetings fetched from backend successfully', {
      component: 'outlookAuth',
      action: 'FETCH_MEETINGS_SUCCESS',
      context: { eventCount: rawEvents.length },
    });

    const ensureUTC = (dtStr) => {
      if (!dtStr) return null;
      if (/Z$|[+-]\d{2}:\d{2}$/.test(dtStr)) return dtStr;
      return `${dtStr}Z`;
    };

    const mappedEvents = rawEvents.map(event => ({
      id: event.id || event.meeting_id || `outlook-${Math.random().toString(36).substr(2, 9)}`,
      summary: event.subject || event.summary || 'Untitled',
      description: event.bodyPreview || event.description || '',
      start: {
        dateTime: ensureUTC(event.start?.dateTime || event.start?.date || event.start_datetime),
        timeZone: 'Asia/Kolkata',
      },
      end: {
        dateTime: ensureUTC(event.end?.dateTime || event.end?.date || event.end_datetime),
        timeZone: 'Asia/Kolkata',
      },
      hangoutLink: event.onlineMeeting?.joinUrl || event.hangoutLink || event.join_url || null,
      location: event.location?.displayName || event.location || null,
      source: 'outlook',
      organizer: event.organizer?.emailAddress?.name || event.organizer || null,
    }));
    return { connected: true, events: mappedEvents };
  } catch (error) {
    if (error.message.includes('Authentication required')) throw error;
    logger.error('Error fetching meetings from backend', {
      component: 'outlookAuth',
      action: 'FETCH_MEETINGS_ERROR',
      error,
    });
    return { connected: false, events: [], error: error.message };
  }
}